"use client";

import { useEffect } from "react";
import { useEditorContext } from "@/src/contexts/editorContext";
import { LOCAL_STORAGE_SYNC_EVENT, announceLocalStorageChange } from "@/src/hooks/useLocalStorageState";

export const THEME_STORAGE_KEY = "qk-theme";
// Every theme and whether it is a light or a dark one. A theme's colours live
// in QuarkleDesign.css under html[data-theme="<name>"]; nothing else knows them.
export const THEMES = { paper: "light", midnight: "dark", dusk: "dark", ember: "dark" };

// The stored choice: a theme name or "system"; unset means system. The old
// "isLightMode" key (true / false / "system") is read once and retired.
export function loadTheme() {
  try {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    if (saved) return saved in THEMES ? saved : "system";
    const legacy = JSON.parse(localStorage.getItem("isLightMode"));
    if (legacy === null) return "system";
    const theme = legacy === true ? "paper" : legacy === false ? "midnight" : legacy in THEMES ? legacy : "system";
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
      localStorage.removeItem("isLightMode");
    } catch {
      // The choice still applies this session; the migration retries next load.
    }
    return theme;
  } catch {
    return "system";
  }
}

export function saveTheme(theme) {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
    announceLocalStorageChange(THEME_STORAGE_KEY);
  } catch {
    // The in-memory theme still applies when browser storage is unavailable.
  }
}

export const resolveTheme = (theme, osDark) => (theme === "system" ? (osDark ? "midnight" : "paper") : theme);

// The whole hand-off to CSS: the theme's name and its appearance on <html>.
export function stampTheme(theme) {
  document.documentElement.dataset.theme = theme;
  document.documentElement.dataset.appearance = THEMES[theme];
}

export default function ThemeProvider({ children }) {
  const { theme, dispatch } = useEditorContext();

  // The stored choice into the context: on mount, and whenever it changes
  // under us (the settings control, or the server's copy at login).
  useEffect(() => {
    const apply = () => dispatch({ type: "theme/set", payload: loadTheme() });
    apply();
    const onSync = (event) => {
      if (event.detail === THEME_STORAGE_KEY) apply();
    };
    window.addEventListener(LOCAL_STORAGE_SYNC_EVENT, onSync);
    return () => window.removeEventListener(LOCAL_STORAGE_SYNC_EVENT, onSync);
  }, [dispatch]);

  // Stamp the resolved theme; "system" follows the OS, live. Storage is the
  // truth here, since the context still says "system" on the first render.
  useEffect(() => {
    const query = window.matchMedia("(prefers-color-scheme: dark)");
    const apply = () => stampTheme(resolveTheme(loadTheme(), query.matches));
    apply();
    if (theme !== "system") return undefined;
    query.addEventListener("change", apply);
    return () => query.removeEventListener("change", apply);
  }, [theme]);

  return children;
}
